import React from 'react';

import hoistNonReactStatic from 'hoist-non-react-statics';
import queryString from 'query-string';
import { connect } from 'react-redux';

import { Redirect } from 'wanderu-component-react-router-link';
import {
    selectCurrency,
    selectLocale,
    i18nCoordinator,
} from 'wanderu-i18n';
import { removeLanguagePath } from 'wanderu-utils';

function getDisplayCurrency(search) {
    const currency = queryString.parse(search).cur;
    return currency
        ? currency.toUpperCase()
        : undefined;
}

function getUrlCurrency(displayCurrency, locale, localeToCurrencyMap) {
    return displayCurrency || localeToCurrencyMap[locale];
}

function getUrlLocale({
    displayLocale,
    path,
    homepagePath,
    defaultLocale,
}) {
    const isHomepage = path === homepagePath;
    return (isHomepage && !displayLocale)
        ? defaultLocale
        : displayLocale;
}

function formatCurrencyForDisplay(currency, locale, localeToCurrencyMap) {
    return localeToCurrencyMap[locale] === currency
        ? undefined
        : currency;
}

function urlIsInSync(opts, localeRedirectBlacklist) {
    const {
        urlCurrency,
        urlLocale,
        reduxCurrency,
        reduxLocale,
        path,
    } = opts;

    if (!!urlCurrency && (reduxCurrency !== urlCurrency)) {
        return false;
    }

    const isBlacklisted = localeRedirectBlacklist.some((matchFn) => {
        return matchFn(path);
    });

    if (!isBlacklisted && (reduxLocale !== urlLocale)) {
        return false;
    }

    if (path === '/en-us/') {
        return false;
    }

    return true;
}

function i18nRedirect(Component, opts) {
    const {
        defaultLocale,
        knownBasePaths,
        homepagePath,
        localeToCurrencyMap,
        localeRedirectBlacklist,
    } = opts;

    class I18nRedirect extends React.Component {
        constructor(props) {
            super(props);

            const {
                currency: reduxCurrency,
                locale: reduxLocale,
                location,
            } = this.props;

            const { locale: displayLocale } = this.props.match.params;
            const displayCurrency = getDisplayCurrency(location.search);

            const urlLocale = getUrlLocale({
                defaultLocale,
                displayLocale,
                homepagePath,
                path: location.pathname,
            });
            const urlCurrency = getUrlCurrency(displayCurrency, urlLocale, localeToCurrencyMap);

            const urlInSync = urlIsInSync({
                path: location.pathname,
                reduxCurrency,
                reduxLocale,
                urlCurrency,
                urlLocale,
            }, localeRedirectBlacklist);

            this.state = {
                push: false,
                redirect: !urlInSync,
            };
        }

        componentDidUpdate(prevProps) {
            const {
                currency: reduxCurrency,
                locale: reduxLocale,
                location,
                match,
            } = this.props;
            const {
                currency: prevReduxCurrency,
                locale: prevReduxLocale,
                location: prevLocation,
                match: prevMatch,
            } = prevProps;

            const displayCurrency = getDisplayCurrency(location.search);
            const prevDisplayCurrency = getDisplayCurrency(prevLocation.search);
            const { locale: displayLocale } = match.params;
            const { locale: prevDisplayLocale } = prevMatch.params;

            const urlLocale = getUrlLocale({
                defaultLocale,
                displayLocale,
                homepagePath,
                path: location.pathname,
            });
            const prevUrlLocale = getUrlLocale({
                defaultLocale,
                displayLocale: prevDisplayLocale,
                homepagePath,
                path: prevLocation.pathname,
            });
            const urlCurrency = getUrlCurrency(displayCurrency, urlLocale, localeToCurrencyMap);
            const prevUrlCurrency = getUrlCurrency(
                prevDisplayCurrency,
                prevUrlLocale,
                localeToCurrencyMap,
            );

            const urlInSync = urlIsInSync({
                path: location.pathname,
                reduxCurrency: reduxCurrency,
                reduxLocale: reduxLocale,
                urlCurrency: urlCurrency,
                urlLocale: urlLocale,
            }, localeRedirectBlacklist);

            if (urlInSync) {
                return this.resetRedirectState();
            }

            const urlCurrencyChanged = urlCurrency !== prevUrlCurrency;
            const urlLocaleChanged = urlLocale !== prevUrlLocale;

            if (urlCurrencyChanged || urlLocaleChanged) {
                return i18nCoordinator.update({
                    currency: urlCurrency,
                    locale: urlLocale,
                });
            }

            const reduxCurrencyChanged = reduxCurrency !== prevReduxCurrency;
            const reduxLocaleChanged = reduxLocale !== prevReduxLocale;
            if (reduxCurrencyChanged || reduxLocaleChanged) {
                return this.setState(() => ({ push: true, redirect: true }));
            }

            const pathChanged = prevLocation.pathname !== location.pathname;
            if (pathChanged) {
                return this.setState(() => ({ push: false, redirect: true }));
            }

            return undefined;
        }

        resetRedirectState({ push = true } = {}) {
            if (!this.state.redirect && this.state.push) {
                return;
            }

            this.setState(() => ({
                push,
                redirect: false,
            }));
        }

        urlHasNoLocale() {
            const { pathname: url } = this.props.location;
            return knownBasePaths.some((matchFn) => matchFn(url));
        }

        render() {
            const {
                currency,
                locale,
                location: {
                    pathname,
                    search,
                },
            } = this.props;

            const {
                redirect,
                push,
            } = this.state;

            let href = removeLanguagePath(pathname);

            if (this.urlHasNoLocale()) {
                href = pathname;
            }

            const displayCurrency = formatCurrencyForDisplay(currency, locale, localeToCurrencyMap);
            let queryParams = queryString.parse(search);
            queryParams.cur = displayCurrency;

            return (
                <React.Fragment>
                    {redirect && (
                        <Redirect
                            href={href}
                            push={push}
                            queries={queryParams}
                        />
                    )}
                    <Component {...this.props} />
                </React.Fragment>
            );
        }
    }

    const HoistedI18nRedirect = hoistNonReactStatic(I18nRedirect, Component);
    return connect(mapStateToProps)(HoistedI18nRedirect);
}

function mapStateToProps(state) {
    return {
        currency: selectCurrency(state),
        locale: selectLocale(state),
    };
}

export {
    mapStateToProps,
};

export default i18nRedirect;
